import { graphql } from '../gql';

/**
 * Every operation this console sends, in one place.
 *
 * `graphql()` is the code generator's tagged builder: it returns a
 * `TypedDocumentNode`, so `useQuery(AdminUsersDocument, …)` types both its
 * variables and its result from the schema with no generics at the call site.
 * After changing anything here, run `make codegen`.
 */

export const AdminLoginDocument = graphql(`
  mutation AdminLogin($input: AdminLoginInput!) {
    adminLogin(input: $input) {
      accessToken
      tokenType
      expiresIn
      admin {
        id
        email
        createdAt
        lastLoginAt
      }
    }
  }
`);

export const AdminMeDocument = graphql(`
  query AdminMe {
    adminMe {
      id
      email
      createdAt
      lastLoginAt
    }
  }
`);

export const AdminStatsDocument = graphql(`
  query AdminStats {
    adminStats {
      usersTotal
      usersPlus
      usersToday
      usersWeek
      storiesTotal
      storiesToday
      storiesWeek
      storiesByStatus {
        status
        count
      }
    }
  }
`);

export const AdminUsersDocument = graphql(`
  query AdminUsers($search: String, $plan: Plan, $role: UserRole, $limit: Int, $offset: Int) {
    adminUsers(search: $search, plan: $plan, role: $role, limit: $limit, offset: $offset) {
      total
      items {
        id
        email
        providers
        role
        plan
        locale
        storiesCount
        createdAt
      }
    }
  }
`);

export const AdminUserDocument = graphql(`
  query AdminUser($id: ID!) {
    adminUser(id: $id) {
      id
      email
      providers
      role
      plan
      locale
      storiesCount
      createdAt
    }
  }
`);

export const AdminUpdateUserDocument = graphql(`
  mutation AdminUpdateUser($id: ID!, $input: AdminUpdateUserInput!) {
    adminUpdateUser(id: $id, input: $input) {
      id
      email
      providers
      role
      plan
      locale
      storiesCount
      createdAt
    }
  }
`);

export const AdminStoriesDocument = graphql(`
  query AdminStories(
    $status: StoryStatus
    $userId: ID
    $mode: StoryMode
    $limit: Int
    $offset: Int
  ) {
    adminStories(status: $status, userId: $userId, mode: $mode, limit: $limit, offset: $offset) {
      total
      items {
        id
        mode
        status
        title
        locale
        createdAt
        user {
          id
          email
        }
      }
    }
  }
`);

export const AdminStoryDocument = graphql(`
  query AdminStory($id: ID!) {
    adminStory(id: $id) {
      id
      mode
      status
      title
      text
      error
      errorDetail
      draft
      locale
      createdAt
      updatedAt
      user {
        id
        email
        providers
        role
        plan
      }
    }
  }
`);

export const AdminRetryStoryDocument = graphql(`
  mutation AdminRetryStory($id: ID!) {
    adminRetryStory(id: $id) {
      id
      status
      error
      errorDetail
      updatedAt
    }
  }
`);

/**
 * Write the story now, in the request. Synchronous by design -- the operator is
 * waiting for the text, not for a queue -- so the call can take a couple of
 * minutes.
 *
 * The answer carries the same id, which is why nothing here refetches: Apollo
 * merges it into the cached `AdminStory` and the detail page re-renders with
 * the new text.
 */
export const AdminGenerateStoryDocument = graphql(`
  mutation AdminGenerateStory($id: ID!) {
    adminGenerateStory(id: $id) {
      id
      status
      title
      text
      error
      errorDetail
      updatedAt
    }
  }
`);

export const AdminCatalogDocument = graphql(`
  query AdminCatalog($collection: CatalogCollection!) {
    adminCatalog(collection: $collection) {
      collection
      id
      labels {
        ru
        en
      }
      category
      characterType
      artworkUrl
      description
      promptDescription
      sortOrder
    }
  }
`);

export const AdminUpsertCatalogItemDocument = graphql(`
  mutation AdminUpsertCatalogItem($collection: CatalogCollection!, $input: AdminCatalogItemInput!) {
    adminUpsertCatalogItem(collection: $collection, input: $input) {
      collection
      id
      labels {
        ru
        en
      }
      category
      characterType
      artworkUrl
      description
      promptDescription
      sortOrder
    }
  }
`);

/**
 * Upserts every row of every table sent, by identifier. Reported per table,
 * whether or not the call actually populated it, so the screen never has to
 * guess which ones to render.
 */
export const AdminImportCatalogDocument = graphql(`
  mutation AdminImportCatalog($input: AdminCatalogImportInput!) {
    adminImportCatalog(input: $input) {
      characters {
        created
        updated
        errors {
          index
          id
          reason
        }
      }
      places {
        created
        updated
        errors {
          index
          id
          reason
        }
      }
      conflicts {
        created
        updated
        errors {
          index
          id
          reason
        }
      }
      morals {
        created
        updated
        errors {
          index
          id
          reason
        }
      }
      traits {
        created
        updated
        errors {
          index
          id
          reason
        }
      }
    }
  }
`);

/**
 * A one-shot permission to put one file in the object store. The console never
 * sends the file to the API: it POSTs the returned `fields` plus the file
 * straight to `url` and saves `publicUrl` on the row.
 */
export const AdminCreateUploadDocument = graphql(`
  mutation AdminCreateUpload($input: AdminCreateUploadInput!) {
    adminCreateUpload(input: $input) {
      url
      fields
      key
      publicUrl
      expiresAt
      maxBytes
    }
  }
`);

/** Throws away an object the operator uploaded and then decided not to save. */
export const AdminDeleteUploadDocument = graphql(`
  mutation AdminDeleteUpload($key: String!) {
    adminDeleteUpload(key: $key)
  }
`);

export const AdminDeleteCatalogItemDocument = graphql(`
  mutation AdminDeleteCatalogItem($collection: CatalogCollection!, $id: ID!) {
    adminDeleteCatalogItem(collection: $collection, id: $id)
  }
`);

export const AdminReorderCatalogDocument = graphql(`
  mutation AdminReorderCatalog($collection: CatalogCollection!, $ids: [String!]!) {
    adminReorderCatalog(collection: $collection, ids: $ids) {
      collection
      id
      labels {
        ru
        en
      }
      category
      characterType
      artworkUrl
      sortOrder
    }
  }
`);

/**
 * The console's own settings: the story prompt and the model. The OpenRouter
 * key is not among them -- it is `OPENROUTER_API_KEY` in the API's environment,
 * which nothing here can read or write.
 */
export const AdminSettingsDocument = graphql(`
  query AdminSettings {
    adminSettings {
      storyPromptKind
      storyPromptScary
      defaultStoryPromptKind
      defaultStoryPromptScary
      promptVariables
      termsRu
      termsEn
      openRouterModel
      updatedAt
    }
  }
`);

/**
 * Every field comes back, because the form re-renders from the answer rather
 * than from what it just sent: the API is the one that knows what was stored.
 */
export const AdminUpdateSettingsDocument = graphql(`
  mutation AdminUpdateSettings($input: AdminSettingsInput!) {
    adminUpdateSettings(input: $input) {
      storyPromptKind
      storyPromptScary
      defaultStoryPromptKind
      defaultStoryPromptScary
      promptVariables
      termsRu
      termsEn
      openRouterModel
      updatedAt
    }
  }
`);

/**
 * The catalogue behind the model page's dropdown. Read on every visit; the API
 * caches the fetch, so this is a round trip to it and not to OpenRouter.
 */
export const AdminOpenRouterModelsDocument = graphql(`
  query AdminOpenRouterModels {
    adminOpenRouterModels {
      id
      name
      promptPrice
      completionPrice
      contextLength
    }
  }
`);

/**
 * One trial run. A mutation because it spends money: nothing may replay it, and
 * nothing caches its answer.
 */
export const AdminTestModelDocument = graphql(`
  mutation AdminTestModel($input: AdminTestModelInput!) {
    adminTestModel(input: $input) {
      model
      text
      promptTokens
      completionTokens
      totalTokens
      cost
      costEstimated
      durationMs
    }
  }
`);

/** "Write Us" messages (TAL-66/TAL-62), newest first. */
export const AdminSupportMessagesDocument = graphql(`
  query AdminSupportMessages($limit: Int, $offset: Int) {
    adminSupportMessages(limit: $limit, offset: $offset) {
      total
      items {
        id
        text
        createdAt
        readAt
        forwardedAt
        user {
          id
          email
        }
      }
    }
  }
`);

/**
 * Idempotent on the API's side: marking an already-read message read again
 * leaves its `readAt` alone, so this is safe to fire more than once.
 */
export const AdminMarkSupportMessageReadDocument = graphql(`
  mutation AdminMarkSupportMessageRead($id: ID!) {
    adminMarkSupportMessageRead(id: $id) {
      id
      readAt
    }
  }
`);

/** "More apps for kids" cross-promo list (TAL-68/TAL-63), in display order. */
export const AdminCrossPromoAppsDocument = graphql(`
  query AdminCrossPromoApps {
    adminCrossPromoApps {
      id
      name
      artworkUrl
      googlePlayUrl
      appStoreUrl
      sortOrder
    }
  }
`);

export const AdminCreateCrossPromoAppDocument = graphql(`
  mutation AdminCreateCrossPromoApp($input: CrossPromoAppInput!) {
    adminCreateCrossPromoApp(input: $input) {
      id
      name
      artworkUrl
      googlePlayUrl
      appStoreUrl
      sortOrder
    }
  }
`);

export const AdminUpdateCrossPromoAppDocument = graphql(`
  mutation AdminUpdateCrossPromoApp($id: ID!, $input: CrossPromoAppInput!) {
    adminUpdateCrossPromoApp(id: $id, input: $input) {
      id
      name
      artworkUrl
      googlePlayUrl
      appStoreUrl
      sortOrder
    }
  }
`);

export const AdminDeleteCrossPromoAppDocument = graphql(`
  mutation AdminDeleteCrossPromoApp($id: ID!) {
    adminDeleteCrossPromoApp(id: $id)
  }
`);

export const AdminReorderCrossPromoAppsDocument = graphql(`
  mutation AdminReorderCrossPromoApps($ids: [ID!]!) {
    adminReorderCrossPromoApps(ids: $ids) {
      id
      name
      artworkUrl
      googlePlayUrl
      appStoreUrl
      sortOrder
    }
  }
`);
